import { foldSearch } from "../searchFold";

/**
 * Processes brand tree API data into TreeSelect format
 * Converts hierarchical company/brand/subbrand data into tree nodes for antd TreeSelect component
 * Filters out duplicate brands and subbrands based on names
 *
 * @param {any} apiData - Raw API data containing companies array with brands and subbrands
 * @returns {any[]} Array of tree nodes with title, value, key, and children properties for TreeSelect
 *
 * @example
 * const treeData = processApiDataToTree({
 *   companies: [{
 *     brands: [{
 *       brand: "Coca Cola",
 *       subbrands: [{ subbrand: "Diet Coke" }]
 *     }]
 *   }]
 * });
 */
export const processApiDataToTree = (apiData: any): any[] => {
  if (!apiData?.companies || !Array.isArray(apiData.companies)) {
    return [];
  }

  const treeData: any[] = [];
  // Use Set to filter out duplicate brands (based on brand name)
  const seenBrands = new Set<string>();

  apiData.companies.forEach((company: any) => {
    if (company.brands && Array.isArray(company.brands)) {
      company.brands.forEach((brand: any) => {
        // Check if brand already exists
        if (seenBrands.has(brand.brand)) {
          return; // Skip duplicate brand
        }
        seenBrands.add(brand.brand);

        const brandNode: any = {
          title: brand.brand, // Use simple text title
          value: brand.brand, // Keep original value for selection
          key: brand.brand, // Use same as value to avoid warning
          type: "brand",
          content: [{ brand: brand.brand, subbrand: null }],
        };

        // Add subbrands as children if they exist
        if (
          brand.subbrands &&
          Array.isArray(brand.subbrands) &&
          brand.subbrands.length > 0
        ) {
          // Use Set to filter out duplicate subbrands (based on brand + subbrand name combination)
          const seenSubbrands = new Set<string>();
          const uniqueSubbrands = brand.subbrands.filter((subbrand: any) => {
            const subValue = `${brand.brand}_${subbrand.subbrand}`;
            if (seenSubbrands.has(subValue)) {
              // Duplicate found, skip it
              return false;
            }
            seenSubbrands.add(subValue);
            return true;
          });

          brandNode.children = uniqueSubbrands.map((subbrand: any) => {
            const subValue = `${brand.brand}_${subbrand.subbrand}`;

            return {
              title: subbrand.subbrand, // Use simple text title
              value: subValue, // Keep original value for selection
              key: subValue, // Use same as value to avoid warning
              type: "subbrand",
              parentBrand: brand.brand,
              content: [{ brand: brand.brand, subbrand: subbrand.subbrand }],
            };
          });
        }

        treeData.push(brandNode);
      });
    }
  });

  return treeData;
};

  /**
   * Gets filtered brand options based on search value for TreeSelect dropdown
   * Filters brands and subbrands that match the search term (case-insensitive)
   * Includes parent brands if they match or if any of their children match
   *
   * @param {string} searchValue - Search term to filter brands by (empty string returns all brands)
   * @returns {any[]} Array of filtered brand tree nodes with matching titles or children
   *
   * @example
   * const filtered = getFilteredBrandOptions("coca");
   * // Returns brands containing "coca" in name or having subbrands containing "coca"
   */
  export const getFilteredBrandOptions = (brandOptions: any[], searchValue: string) => {
    if (!searchValue) {
      return brandOptions;
    }

    const query = foldSearch(searchValue);
    const filterNode = (node: any): any | null => {
      const titleMatch = foldSearch(node.title).includes(query);
      const hasMatchingChildren = node.children?.some((child: any) =>
        foldSearch(child.title).includes(query)
      );

      if (titleMatch || hasMatchingChildren) {
        return {
          ...node,
          children:
            node.children
              ?.map((child: any) => filterNode(child))
              .filter(Boolean) || [],
        };
      }

      return null;
    };

    const filteredResult = brandOptions
      .map((node) => filterNode(node))
      .filter(Boolean);
    return filteredResult;
  };

    /**
   * Normalizes a string for safe comparison operations
   * Trims whitespace, converts to lowercase, and standardizes spacing/punctuation
   * Used for consistent brand name matching across different input formats
   *
   * @param {string} str - The string to normalize
   * @returns {string} Normalized string ready for comparison
   *
   * @example
   * normalizeForComparison("  Coca-Cola  "); // "coca cola"
   * normalizeForComparison("PEPSI_cola"); // "pepsi cola"
   */
    export const normalizeForComparison = (str: string) => {
      return str
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ")
        .replace(/[-_]/g, " ");
    };